import React, { useContext } from 'react';
import { Box, Stack } from '@mui/material';
import { Link } from '@tanstack/react-router';

import { CameraStateContext } from 'context/state/camera';

import { CameraVideo } from 'components/camera/CameraVideo';
import { CameraControlDeckType } from 'components/camera/CameraControlDeck';
import { CameraVideoThumbnail } from 'components/camera/CameraVideoThumbnail';
import { Sound } from 'components/camera/controls/Sound';
import { useAspenLogger } from 'hooks/useAspenLogger';

import { AppDeviceLeaf } from 'types/common/leaf';

import styles from './cameraTile.module.scss';

export type CameraTileType = 'preview' | 'normal';

interface CameraTileProps extends React.HTMLAttributes<HTMLDivElement> {
  leaf: AppDeviceLeaf;
  cameraTileType?: CameraTileType;
  controlDeckType?: CameraControlDeckType;
  isFocusedView?: boolean;
}

export const CameraTile = ({ className, leaf, cameraTileType, controlDeckType, isFocusedView }: CameraTileProps) => {
  const { soundEnabled } = useContext(CameraStateContext);
  const { logAspenEvent } = useAspenLogger();

  return (
    <Box className={`${styles.cameraTile}${className ? ` ${className}` : ''}`} data-testid="component-camera-tile">
      {cameraTileType === 'normal' && (
        <Stack className={styles.title} direction="row" justifyContent="left" alignItems="center">
          {leaf.name}
          <Sound leafId={leaf.id} soundEnabled={soundEnabled} />
        </Stack>
      )}
      {cameraTileType === 'preview' ? (
        <>
          <Link
            className={styles.previewLink}
            to="/$camera"
            params={{ camera: leaf.id }}
            onClick={() => {
              logAspenEvent({
                log_id: 'iot.web.stream.action',
                payload: {
                  device_id: leaf?.id,
                  action: 'focused_view_change',
                },
              });
            }}
          />
          <CameraVideoThumbnail key={`${leaf.id}-cvt`} leaf={leaf} />
        </>
      ) : (
        <CameraVideo
          key={`${leaf.id}-cv`}
          leaf={leaf}
          controlDeckType={controlDeckType}
          isFocusedView={isFocusedView}
        />
      )}
    </Box>
  );
};
